/**
 * erro-amigável.ts — tradutor de erros do Supabase/Postgres pra mensagens
 * amigaveis em PT-BR voltadas ao usuário final.
 *
 * Regra de ouro: NUNCA leak de mensagem tecnica pro toast.
 *
 * Uso:
 *   import { traduzirErroSupabase } from '@octawash/shared';
 *   toast.error('Falha', { description: traduzirErroSupabase(err) });
 *
 * Também aceita objetos opacos (web, edge functions, RPC). Se não
 * reconhecer, retorna mensagem generica "Algo deu errado..."
 *
 * Mantenha o catalogo CONSTRAINT_PT_BR atualizado quando criar nova
 * constraint UNIQUE/CHECK/FK (regra documentada em CLAUDE.md).
 */

// =============================================================================
// Catalogo de constraints conhecidas -> mensagem amigável
// =============================================================================
//
// Quando o Postgres dispara "duplicate key violates unique constraint NOME",
// a gente traduz NOME pra mensagem útil.
//
// Adicione novas entradas conforme criar constraints. Se não tiver entrada,
// cai no fallback generico (que também é PT-BR mas menos especifico).

const CONSTRAINT_PT_BR: Record<string, string> = {
  // Veículos
  uk_veiculo_placa_ativa:
    'Está placa já está cadastrada em outra conta ativa. Se o veículo é seu, contate o suporte.',
  uk_modelo_por_marca: 'Este modelo já existe para essa marca.',
  tab_marcas_veiculo_nom_marca_key: 'Essa marca já está cadastrada.',

  // Clientes / Auth
  uk_clientes_cpf_cnpj: 'Já existe um cadastro com esse CPF/CNPJ.',
  tab_clientes_cod_auth_user_key: 'Você já tem um cadastro nesse tenant.',
  tab_cliente_logins_adicionais_cod_auth_user_key:
    'Esse usuário já é login adicional de algum cliente.',

  // Tenants
  tab_tenants_cod_tenant_key: 'Esse código de tenant já está em uso.',
  tab_tenants_des_subdominio_key: 'Esse subdominio já está em uso.',

  // Planos / promoções
  tab_planos_cod_plano_key: 'Já existe um plano com esse código.',
  uk_plano_tipo_lavagem: 'Esse plano já tem um preço cadastrado para esse tipo de lavagem.',
  uk_preco_avulso_unidade: 'Já existe um preço avulso cadastrado para essa unidade e tipo.',
  tab_promocoes_cod_promocao_key: 'Já existe uma promoção com esse código.',
  uk_assin_promo: 'Essa promoção já está aplicada nessa assinatura.',
  uk_faixa_plano_qtd: 'Já existe uma faixa corporativa com essa quantidade nesse plano.',

  // Asaas integration
  tab_assinaturas_cod_id_externo_asaas_key:
    'Conflito ao sincronizar com o Asaas — recarregue e tente de novo.',
  tab_clientes_asaas_cod_id_externo_asaas_key: 'Cliente já sincronizado no Asaas.',
  tab_clientes_asaas_seq_cliente_key: 'Esse cliente já tem vinculo com o Asaas.',
  tab_pagamentos_cod_id_externo_asaas_key: 'Esse pagamento já foi registrado.',

  // Eventos / webhooks
  tab_eventos_asaas_cod_id_externo_key: 'Evento já processado.',
  tab_eventos_uazapi_cod_id_externo_key: 'Mensagem já registrada.',
  tab_comandos_upwash_cod_idempotencia_key: 'Comando já enviado.',

  // Corporativo
  uk_cc_corp: 'Esse centro de custo já existe nessa empresa.',
  uk_corp_cliente: 'Esse cliente já está vinculado a uma empresa.',
  uk_fat_corp_periodo: 'Já existe faturamento desse período para essa empresa.',
  uk_gestor_corp_user: 'Esse gestor já está vinculado a essa empresa.',

  // Operação
  tab_unidades_cod_unidade_key: 'Já existe uma unidade com esse código.',
  tab_maquinas_cod_maquina_key: 'Já existe uma maquina com esse código.',
  tab_cameras_lpr_cod_camera_key: 'Já existe uma camera com esse código.',
  tab_totens_cod_totem_key: 'Já existe um totem com esse código.',
  tab_tipos_lavagem_cod_tipo_lavagem_key: 'Já existe um tipo de lavagem com esse código.',
  tab_fila_unidade_seq_maquina_key: 'Essa maquina já está na fila.',

  // Fidelidade
  tab_recompensas_fidelidade_cod_recompensa_key: 'Já existe uma recompensa com esse código.',
  tab_indicacoes_cod_codigo_indicacao_key:
    'Conflito ao gerar código de indicação — tente novamente.',
  uq_avaliacao_cliente_lavagem: 'Essa lavagem já foi avaliada.',

  // Mensagens / templates
  tab_templates_mensagem_cod_template_key: 'Já existe um template com esse código.',
  tab_uazapi_instancias_cod_instancia_key: 'Já existe instancia com esse código.',

  // Sistema
  uk_param_sistema_tenant_cod: 'Esse parametro já está cadastrado nesse tenant.',
  tab_feature_flags_cod_feature_key: 'Essa feature flag já existe.',
  uq_feature_flag_tenant: 'Essa feature já está configurada nesse tenant.',
  uq_dispositivos_push_token: 'Esse dispositivo já está registrado.',
  uk_versoes_termos_tenant_versao: 'Essa versão de termos já existe.',
  uk_banner_cod_tenant: 'Já existe um banner com esse código nesse tenant.',
  tab_estrategias_winback_cod_estrategia_key:
    'Já existe uma estrategia de win-back com esse código.',
  tab_convites_login_adicional_des_token_key: 'Token de convite já usado ou inválido.',
  tab_veiculos_corporativos_seq_veiculo_key: 'Esse veículo já está vinculado a uma empresa.',
  tab_pagamentos_avulsos_seq_pagamento_key: 'Esse pagamento avulso já está registrado.',
};

// =============================================================================
// Mensagens conhecidas geradas por triggers (RAISE EXCEPTION) — tratamos
// por substring, já que o código não vem padronizado.
// =============================================================================

const TRIGGER_REGEX: Array<{ pattern: RegExp; mensagem: string }> = [
  {
    pattern: /Formato de placa inválido/i,
    mensagem: 'Formato de placa inválido. Use padrao Mercosul (ABC1D23) ou antigo (ABC-1234).',
  },
  {
    pattern: /Cliente .* nao encontrado/i,
    mensagem: 'Seu cadastro não foi encontrado. Faca logout e login de novo.',
  },
  {
    pattern: /Troca de placa bloqueada por carência/i,
    mensagem:
      'Você alterou a placa recentemente. Aguarde o período de carência ou contate o suporte.',
  },
  {
    pattern: /Tipo de veículo não suportado/i,
    mensagem: 'Tipo de veículo não suportado pelo OctaWash.',
  },
  {
    pattern: /seq_tenant.*nao bate/i,
    mensagem: 'Conflito de tenant. Faca logout e login de novo.',
  },
  {
    pattern: /Saldo insuficiente|Pontos insuficientes/i,
    mensagem: 'Pontos insuficientes para esse resgate.',
  },
  {
    pattern: /Código expirado/i,
    mensagem: 'Esse código expirou. Gere um novo.',
  },
  {
    pattern: /Codigo invalido|Codigo nao encontrado/i,
    mensagem: 'Código inválido ou já utilizado.',
  },
  {
    pattern: /Veículo não elegivel/i,
    mensagem: 'Esse veículo não está elegivel para lavagem agora. Verifique sua assinatura.',
  },
  {
    pattern: /Assinatura .* nao ativa|Sem assinatura ativa/i,
    mensagem: 'Sua assinatura não está ativa no momento.',
  },
  {
    pattern: /Limite de lavagens/i,
    mensagem: 'Você já atingiu o limite de lavagens do seu plano nesse período.',
  },
];

// =============================================================================
// Mapeamento de códigos PostgREST / Postgres
// =============================================================================
//
// Códigos:
//   - 23505 = unique_violation (PG)        -> tratado via constraint name acima
//   - 23503 = foreign_key_violation        -> "vinculo não encontrado"
//   - 23514 = check_violation              -> já vem com hint no MESSAGE
//   - 23502 = not_null_violation           -> "campo obrigatório ausente"
//   - 42501 = insufficient_privilege (RLS) -> "sem permissao"
//   - PGRST116 = no rows returned          -> "não encontrado"
//   - PGRST301 = JWT expired               -> "sessao expirou"

const CODIGO_PG_PT_BR: Record<string, string> = {
  '23502': 'Algum campo obrigatório está vazio.',
  '23503': 'Referência não encontrada. Recarregue a tela e tente de novo.',
  '23514': 'Algum valor não atende as regras de validação.',
  '42501': 'Você não tem permissao para essa ação.',
  PGRST116: 'Nada encontrado para essa consulta.',
  PGRST301: 'Sua sessao expirou. Faca login novamente.',
  '23P01': 'Conflito de concorrencia. Recarregue e tente de novo.',
  '42P01': 'Recurso indisponível no momento. Tente novamente mais tarde.',
};

// =============================================================================
// Funcao pública
// =============================================================================

export interface ErroLike {
  message?: string;
  code?: string;
  details?: string;
  hint?: string;
  // Edge function errors / wrappers
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context?: any;
}

function extrairMensagem(err: unknown): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  const e = err as ErroLike;
  return e.message ?? '';
}

function extrairCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as ErroLike;
  if (typeof e.code === 'string') return e.code;
  return null;
}

/**
 * Traduz qualquer erro do Supabase/Postgres pra mensagem amigável PT-BR.
 *
 * Estrategia (em ordem):
 *   1. Códigos conhecidos (23505, 42501, PGRST116, etc)
 *   2. Constraint name conhecida (extraido da mensagem)
 *   3. Padroes de mensagem de trigger RAISE EXCEPTION
 *   4. Fallback generico (mantem confiabilidade — nunca expoe SQL)
 */
export function traduzirErroSupabase(err: unknown): string {
  if (!err) return 'Algo deu errado. Tente novamente em instantes.';

  const mensagem = extrairMensagem(err);
  const code = extrairCode(err);
  const msgLower = mensagem.toLowerCase();

  // 1) Código PG conhecido (PRIORIDADE: 23505 vai pra tratamento por constraint)
  if (code === '23505' || /duplicate key|already exists/i.test(mensagem)) {
    // Extrai nome da constraint (formato: violates unique constraint "NOME")
    const m = mensagem.match(/constraint\s+"([^"]+)"/i);
    const nome = m?.[1];
    if (nome && CONSTRAINT_PT_BR[nome]) return CONSTRAINT_PT_BR[nome];
    return 'Esse dado já está cadastrado. Confira se não é uma duplicata.';
  }

  if (code === '23503' || /foreign key/i.test(mensagem)) {
    return CODIGO_PG_PT_BR['23503']!;
  }
  if (code === '23502' || /not-null|null value in column/i.test(mensagem)) {
    return CODIGO_PG_PT_BR['23502']!;
  }
  if (code === '42501' || /row-level security|new row violates row-level/i.test(mensagem)) {
    return 'Você não tem permissao para essa ação. Confira seu acesso.';
  }
  if (code && CODIGO_PG_PT_BR[code]) return CODIGO_PG_PT_BR[code]!;

  // 2) Padrao trigger conhecido
  for (const { pattern, mensagem: m } of TRIGGER_REGEX) {
    if (pattern.test(mensagem)) return m;
  }

  // 3) Network / fetch
  if (/networkerror|failed to fetch|load failed/i.test(msgLower)) {
    return 'Sem conexao. Verifique sua internet e tente novamente.';
  }
  if (/timeout|timed out/i.test(msgLower)) {
    return 'A operação demorou demais. Tente novamente.';
  }

  // 4) Edge function vagamente reportada
  if (/edge function/i.test(msgLower)) {
    return 'Serviço indisponível no momento. Tente em instantes.';
  }

  // 5) Fallback — se for mensagem PT-BR já amigável (até ~120 chars, sem
  // termos tecnicos), passa direto. Caso contrario, mascara.
  const pareceTecnica =
    mensagem.length > 200 ||
    /pg|sql|postgres|null value|constraint|relation|column|schema|ERRCODE|PGRST/.test(mensagem);
  if (mensagem && !pareceTecnica) return mensagem;

  return 'Algo deu errado. Tente novamente em instantes — se persistir, contate o suporte.';
}
